import { apiError } from '@/renderer/util/notify';
import { Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../common';

interface KotPhysicalNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (physicalKotNumber: string) => void;
  isSubmitting?: boolean;
}

export const KotPhysicalNumberModal: React.FC<KotPhysicalNumberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setValue('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      apiError('Enter the KOT number written on the paper ticket');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close KOT number modal"
      />

      <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-black">
            Enter Physical KOT Number
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4 px-6 pb-6 pt-5">
          <p className="text-sm text-gray-600">
            Enter the number printed on the paper KOT ticket the waiter used for
            this order, so it can be reconciled later.
          </p>

          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800">
              Physical KOT Number
            </p>
            <input
              id="physical-kot-number"
              type="text"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !isSubmitting) handleSubmit();
              }}
              disabled={isSubmitting}
              aria-label="Physical KOT number"
              placeholder="e.g. 128"
              className="h-10 w-full rounded border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-10 text-xs font-semibold uppercase tracking-wide"
            >
              Cancel
            </Button>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="h-10 text-xs font-semibold uppercase tracking-wide"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Confirming
                </>
              ) : (
                'Confirm KOT'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
